import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";

import FormattedNumberInput from "@/components/FormattedNumberInput";
const CATEGORIES = ["Mechanical", "Electrical", "Quality", "Material", "Operator", "Power", "Maintenance", "Other"];
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

export default function Downtime() {
  const [rows, setRows] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = {
    machine_id: "", start_time: new Date().toISOString().slice(0, 16),
    duration_min: 30, reason_category: "Mechanical", reason: "",
    action_taken: "", responsible_person: "",
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [d, m] = await Promise.all([api.get("/downtimes/"), api.get("/machines/")]);
    setRows(d.data); setMachines(m.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/downtimes/", { ...form, start_time: new Date(form.start_time).toISOString() });
      toast.success("Downtime recorded");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/downtimes/${id}`);
    toast.success("Deleted"); load();
  };
  const mcode = (id) => machines.find(m => m.id === id)?.code || "—";

  // Aggregate by category
  const byCat = CATEGORIES.map(c => ({ cat: c, total: rows.filter(r => r.reason_category === c).reduce((a, b) => a + b.duration_min, 0) })).filter(x => x.total > 0);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Loss Analysis</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Downtime Recording</h1>
        </div>
        <button data-testid="new-downtime-btn" onClick={() => setOpen(true)} className="h-10 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
          <PlusIcon size={14} weight="bold" />Record Downtime
        </button>
      </div>

      {byCat.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {byCat.map(c => (
            <div key={c.cat} className="tactical-card p-3">
              <div className="overline text-red-400 mb-1">{c.cat}</div>
              <div className="font-mono text-xl text-white">{c.total}<span className="text-xs text-zinc-500 ml-1">min</span></div>
            </div>
          ))}
        </div>
      )}

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-5 py-3 font-medium">Machine</th>
              <th className="text-left px-5 py-3 font-medium">Start</th>
              <th className="text-right px-5 py-3 font-medium">Duration</th>
              <th className="text-left px-5 py-3 font-medium">Category</th>
              <th className="text-left px-5 py-3 font-medium">Reason</th>
              <th className="text-left px-5 py-3 font-medium">Action</th>
              <th className="text-left px-5 py-3 font-medium">Responsible</th>
              <th className="text-right px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)]" data-testid={`downtime-${r.id}`}>
                <td className="px-5 py-3 font-mono text-white">{mcode(r.machine_id)}</td>
                <td className="px-5 py-3 font-mono text-zinc-400 text-xs">{new Date(r.start_time).toLocaleString()}</td>
                <td className="px-5 py-3 text-right font-mono text-red-400">{r.duration_min}m</td>
                <td className="px-5 py-3 text-zinc-400">{r.reason_category}</td>
                <td className="px-5 py-3 text-zinc-400">{r.reason}</td>
                <td className="px-5 py-3 text-zinc-500">{r.action_taken}</td>
                <td className="px-5 py-3 text-zinc-400">{r.responsible_person}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => remove(r.id)} className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400"><TrashIcon size={14} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-16 text-zinc-500">No downtime entries</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-xl">
          <DialogHeader><DialogTitle className="font-display">Record Downtime</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Machine">
              <Select value={form.machine_id} onValueChange={v => setForm({...form, machine_id: v})}>
                <SelectTrigger data-testid="dt-machine" className={selCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {machines.map(m => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Start Time"><input type="datetime-local" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} className={inp} /></F>
            <F label="Duration (min)"><FormattedNumberInput data-testid="dt-duration" value={form.duration_min} onChange={e => setForm({...form, duration_min: parseFloat(e.target.value) || 0})} className={inp} /></F>
            <F label="Category">
              <Select value={form.reason_category} onValueChange={v => setForm({...form, reason_category: v})}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Reason" className="col-span-2"><input data-testid="dt-reason" value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} className={inp} /></F>
            <F label="Action Taken" className="col-span-2"><input value={form.action_taken} onChange={e => setForm({...form, action_taken: e.target.value})} className={inp} /></F>
            <F label="Responsible" className="col-span-2"><input value={form.responsible_person} onChange={e => setForm({...form, responsible_person: e.target.value})} className={inp} /></F>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-downtime" onClick={save} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function F({ label, children, className = "" }) {
  return <div className={className}><div className="overline mb-1.5">{label}</div>{children}</div>;
}
